/**
 * Liquiditätsplanung v1 (#1315) — the compute core.
 *
 * Pure, client-side, no React/network: anchor balance + bank facts
 * (history) + planned transactions (future) → a daily projected balance
 * series and an Ampel verdict. The "edit → instant recompute" demo beat
 * re-runs this on an optimistically-updated React Query cache, so it
 * MUST stay a pure function.
 *
 * Continuity is structural, not data-dependent: history is walked
 * BACKWARD from the authoritative anchor balance, and the projection is
 * walked FORWARD from the same anchor — so the bridge point is shared
 * and "running balance continuous across the anchor" (success criterion
 * #2) holds by construction, regardless of seed reconciliation.
 */
import type {
  BalanceSeriesResult,
  BankTransaction,
  DailyBalancePoint,
  LiquiditaetConfig,
  MonthVerdict,
  PlannedTransaction,
  StatementBalance,
  StatementResidual,
  Verdict,
  VerdictLevel,
} from './types';

/** Forecast window length, in months after the anchor (rolling WJ). Exported so
 *  the form's fallback-mode date bounds use the SAME horizon as the projection
 *  (no drift between what the curve covers and what the form accepts). */
export const WINDOW_MONTHS = 12;
/** Yellow band: balance within this fraction of |KK-Limit| above the limit. */
const YELLOW_BUFFER_FRACTION = 0.2;

const eur = (n: number): string =>
  new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);

const germanMonth = (ym: string): string => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('de-DE', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const parseUtc = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

/**
 * True only for a canonical, calendar-valid ISO date (``YYYY-MM-DD``). The
 * shape regex alone is not enough — ``'2026-13-40'`` matches it but
 * ``Date.UTC`` *overflow-normalizes* it (→ 2027-02-09) instead of producing
 * NaN, so a bare ``Number.isFinite(getTime())`` check would wrongly pass it and
 * silently anchor the projection on the wrong day. The round-trip equality
 * (``toIso(parseUtc(s)) === s``) is the real validity test: an impossible date
 * never serializes back to itself. The finite guard before ``toIso`` keeps an
 * unparseable value (which the regex already rejects) from throwing in here.
 * Used to keep a malformed planned ``value_date`` ('foo', '2026-13-40') from
 * ever reaching the date math (codex #1364 P2b).
 */
export const isIsoDate = (s: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = parseUtc(s);
  return Number.isFinite(d.getTime()) && toIso(d) === s;
};

const toIso = (date: Date): string => date.toISOString().slice(0, 10);

/** Add ``n`` days to an ISO date (UTC). Exported for the form's fallback-mode
 *  date-bound math so it stays consistent with the projection's day stepping. */
export const addDays = (iso: string, n: number): string => {
  const d = parseUtc(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return toIso(d);
};

/** Add ``n`` months to an ISO date (UTC). Exported alongside ``addDays`` so the
 *  fallback date bounds reuse the projection's exact horizon arithmetic. */
export const addMonths = (iso: string, n: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return toIso(new Date(Date.UTC(y, m - 1 + n, d)));
};

/** First day of an ISO month ``YYYY-MM`` → ``YYYY-MM-01``. */
export const firstDayOfMonth = (ym: string): string => `${ym}-01`;

/** Last calendar day of an ISO month ``YYYY-MM`` (leap-aware). Day 0 of the
 *  next month = the last day of this one. */
export const lastDayOfMonth = (ym: string): string => {
  const [y, m] = ym.split('-').map(Number);
  return toIso(new Date(Date.UTC(y, m, 0)));
};

/** Shift an ISO month ``YYYY-MM`` by ``n`` months → ``YYYY-MM``. */
export const addMonthsToYm = (ym: string, n: number): string => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

/** ``YYYY-MM``, months 01-12 only. Deliberately stricter than ``ym.split('-')``:
 *  ``lastDayOfMonth`` returns an ``Invalid Date`` string for ``'not-a-month'`` and
 *  silently accepts ``'2026-13'`` as January 2027, and the predicate below must never
 *  do either. */
const YM_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * HOF-108 — has the Ist already closed the accounting period this plan row is for?
 *
 * Mirror of the Python ``period_closed_by_horizon``
 * (``backend/src/services/finance/liquiditaet_reads.py``).
 *
 * The engine's istHorizon partition draws actuals up to the horizon and plans after it,
 * and until now trusted ``planSupersededByPayed`` to have removed every overlap first.
 * That guard matches on ``transaction_id`` **identity**, so it only ever sees a plan
 * whose own settlement carries its id — a document-derived expectation and its settling
 * credit. A **top-down BWA plan row** is never linked to the bank lines that fulfil it
 * and never will be, so identity supersession is structurally blind to it: on WF, July
 * 2026's entire BWA revenue (3.099.683,70 €) was re-added on top of July's actuals
 * because the ws2627 plan set's ~45-day payment lag dates ``period_ym`` 2026-07 to
 * ``value_date`` 2026-08-15 — past the horizon. One day of forecast moved
 * +2.420.427,69 €, 1,4x the farm's all-time Ist maximum, and it rolled forward: every
 * horizon advance re-armed it for the next month.
 *
 * The rule identity cannot express is about **periods**: a plan for a month the bank has
 * already closed is a claim about money that is either in the balance already or never
 * arrived — in both cases the Ist is the better witness, so the plan may not be projected
 * on top of it.
 *
 * **Fully closed, and that is a decision (HOF-108 §7 D1).** ``true`` requires the
 * period's LAST calendar day to be at or before the horizon. A plan for the *current*
 * month while the horizon sits mid-month is not closed and still projects: the Ist has
 * only replaced part of it, and suppressing the whole row would delete forecast money —
 * the one direction that can hide an Engpass. What remains double-counted is bounded by
 * the days from the month's first day to the horizon; what this removes was a full month,
 * unbounded, every month.
 *
 * Both arguments are validated rather than trusted, and an unreadable one closes nothing:
 * a malformed ``period_ym`` is *unknown provenance* and suppressing on a guess would
 * silently drop real money, while a malformed horizon sorts arbitrarily against ISO
 * strings — the #1661 trap that already bit ``istHorizon`` itself.
 *
 * Note the shape that is *safe by construction*: with no payment lag,
 * ``period_ym === value_date.slice(0, 7)``, so ``lastDayOfMonth(period_ym) >= value_date
 * > istHorizon`` for every row the partition still holds — a lagless plan can never be
 * closed by this rule. That is why the change is a no-op for every pre-HOF-108 fixture.
 */
export const periodClosedByHorizon = (
  periodYm: string | undefined,
  istHorizon: string,
): boolean => {
  if (!istHorizon || !isIsoDate(istHorizon)) return false;
  if (!periodYm || !YM_RE.test(periodYm)) return false;
  return lastDayOfMonth(periodYm) <= istHorizon;
};

const signedPlanned = (p: PlannedTransaction): number =>
  p.direction === 'income' ? p.amount_eur : -p.amount_eur;

/**
 * Round to cents. Balances accumulate across hundreds of cent-valued
 * txns, so a raw running sum drifts (0.1 + 0.2 = 0.30000000000000004);
 * left unrounded, ``classify``'s exact ``< kkLimit`` could read an
 * at-limit balance as a breach and disagree with the Excel hand-check.
 */
const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Sum amounts onto a per-day map keyed by ISO date. */
function bucketByDay<T>(items: T[], date: (t: T) => string, amount: (t: T) => number): Map<string, number> {
  const m = new Map<string, number>();
  for (const it of items) {
    const k = date(it);
    m.set(k, (m.get(k) ?? 0) + amount(it));
  }
  return m;
}

function neutralVerdict(kkLimitEur: number | null, sentence: string): Verdict {
  return {
    level: 'yellow',
    sentence,
    firstProblemYm: null,
    firstProblemDate: null,
    firstProblemAmountEur: null,
    kkLimitEur,
    monthly: [],
  };
}

function classify(minBalanceEur: number, kkLimit: number): VerdictLevel {
  if (minBalanceEur < kkLimit) return 'red';
  const buffer = YELLOW_BUFFER_FRACTION * Math.abs(kkLimit);
  if (minBalanceEur - kkLimit < buffer) return 'yellow';
  return 'green';
}

const SEVERITY: Record<VerdictLevel, number> = { green: 0, yellow: 1, red: 2 };

/**
 * Build the Ampel verdict from the forecast points + KK-limit. Exported (#2005)
 * so the "Alle Konten" aggregate reuses the EXACT same month-grouping / classify /
 * sentence logic on the summed series (against Σ limits) — body untouched. */
export function buildVerdict(
  futurePoints: DailyBalancePoint[],
  kkLimit: number | null,
): Verdict {
  if (kkLimit == null) {
    return neutralVerdict(
      null,
      'KK-Limit noch nicht hinterlegt — ein Engpass kann nicht bewertet werden.',
    );
  }

  // Group future points by month, keep the intra-month minimum (first occurrence).
  const byMonth = new Map<string, { minBalanceEur: number; minDate: string }>();
  for (const p of futurePoints) {
    const ym = p.date.slice(0, 7);
    const cur = byMonth.get(ym);
    if (cur == null || p.balanceEur < cur.minBalanceEur) {
      byMonth.set(ym, { minBalanceEur: p.balanceEur, minDate: p.date });
    }
  }

  const monthly: MonthVerdict[] = [...byMonth.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([periodYm, { minBalanceEur, minDate }]) => ({
      periodYm,
      minBalanceEur,
      minDate,
      level: classify(minBalanceEur, kkLimit),
    }));

  const overallLevel: VerdictLevel = monthly.reduce<VerdictLevel>(
    (worst, m) => (SEVERITY[m.level] > SEVERITY[worst] ? m.level : worst),
    'green',
  );

  // Headline = the first month AT the overall severity, so the sentence,
  // the firstProblem* fields and the lens default all name the same
  // month. (A red overall must name the first *breach*, not an earlier
  // tight month — else the copy contradicts itself: codex #1324 P2.)
  const headline =
    overallLevel === 'green'
      ? null
      : monthly.find((m) => m.level === overallLevel) ?? null;
  // Index access (not Array.at) — the build targets ES2020 and esbuild
  // won't polyfill .at for older Safari; codex #1324 round-3 P2.
  const lastMonth = monthly.length > 0 ? monthly[monthly.length - 1] : undefined;

  let sentence: string;
  if (overallLevel === 'green' || !headline) {
    sentence = lastMonth
      ? `Grün bis ${germanMonth(lastMonth.periodYm)}: Saldo bleibt über dem KK-Limit.`
      : 'Grün: Saldo bleibt über dem KK-Limit.';
  } else if (overallLevel === 'red') {
    sentence = `Engpass ab ${germanMonth(headline.periodYm)}: ${eur(headline.minBalanceEur)} (unter KK-Limit ${eur(kkLimit)}).`;
  } else {
    const headroom = headline.minBalanceEur - kkLimit;
    sentence = `Eng ab ${germanMonth(headline.periodYm)}: nur ${eur(headroom)} über dem KK-Limit.`;
  }

  return {
    level: overallLevel,
    sentence,
    firstProblemYm: headline?.periodYm ?? null,
    firstProblemDate: headline?.minDate ?? null,
    firstProblemAmountEur: headline?.minBalanceEur ?? null,
    kkLimitEur: kkLimit,
    monthly,
  };
}

/** Default KK-Limit assumed when none is stored: a 5.000 € minimum cash floor
 *  (stored positive — the projected balance must stay *above* it). A real
 *  ``liquiditaet_config`` (a negative overdraft line, like the −50.000 €
 *  fixtures) overrides this the moment it is seeded. */
const DEFAULT_KK_LIMIT_EUR = 5000;

/**
 * Synthesize a default config when none is stored, so the curve + verdict
 * render against the user's rows instead of a dead "no config" state: a 0
 * opening balance, a default 5.000 € KK-Limit, anchored the day before the
 * earliest PLANNED transaction (so every plan row projects forward). Bank facts
 * are deliberately NOT used to pick the anchor — they belong to history and the
 * normal ``booking_date <= anchor`` walk applies them; anchoring before them
 * would drop them (codex P2). NOT persisted — a real ``liquiditaet_config``
 * fact takes over the moment it is seeded. ``null`` when there is no plan to
 * anchor to.
 */
function defaultConfig(planned: PlannedTransaction[]): LiquiditaetConfig | null {
  // Canonical-ISO filter (not just truthy): a malformed-but-non-empty
  // value_date ('foo', '2026-13-40') must never anchor the projection, or the
  // date math throws / silently anchors wrong (codex #1364 P2b).
  const dates = planned.map((p) => p.value_date).filter(isIsoDate);
  if (dates.length === 0) return null;
  const earliest = dates.reduce((min, d) => (d < min ? d : min));
  return {
    anchor_date: addDays(earliest, -1),
    anchor_balance_eur: 0,
    kk_limit_eur: DEFAULT_KK_LIMIT_EUR,
    wj_start_month: 7,
  };
}

/** HOF-97 §6 L5.4 — the slack a per-statement residual is judged at. Deliberately the
 *  same 2 cents as the worker's ``_BALANCE_IDENTITY_TOLERANCE_EUR`` and the Python
 *  port's ``STATEMENT_RESIDUAL_TOLERANCE_EUR``: the write side and the read side ask one
 *  question from two directions, and two thresholds would let a statement be reconciled
 *  by one and contested by the other. */
export const STATEMENT_RESIDUAL_TOLERANCE_EUR = 0.02;

/**
 * The ``end_date`` → authoritative-Endzeile map the series re-anchors on (HOF-97 L5.1).
 * Mirror of ``liquiditaet_reads.statement_anchors``.
 *
 * An entry earns a place only if the bank's own statement of record is unambiguous
 * there. Three ways it is not, all yielding NO anchor rather than a guess:
 *
 * - ``endsaldo_eur`` is not a finite number — an absent Saldo stays absent and is never
 *   read as 0,00 (fabricating a zero anchor would wipe the account to nil);
 * - the row is ``contested`` — the write-side guard already found a divergent
 *   ``(account, end_date)`` group and said so;
 * - the row was ADOPTED rather than addressed (``anchor_adopted``) — a seed-era statement
 *   names no account, and ``statementsForAccount`` routes it to the alias so it can still
 *   advance that account's Ist horizon. Safe while statements only moved a HORIZON; not
 *   safe now that they move MONEY, since the row may attest to a different Konto or be a
 *   legacy aggregate (codex round 2, P1);
 * - two rows share an ``end_date`` with DIFFERENT closing balances. This is the
 *   read-side half of the same guard and it is not redundant: every conflict already
 *   stored predates the write guard, and WF alone carries ten contradictory 30.06
 *   values on one Konto. Without it, shipping L5 would promote each of them to an
 *   anchor and make the served number worse than the one the customer complained about
 *   — which is precisely the ordering hazard §7 names. Equal duplicates are NOT a
 *   conflict: one statement re-ingested under two fragment ids agrees with itself.
 *
 * Keyed on ``end_date`` alone. The account dimension is the caller's — the engine runs
 * once per account — so folding an account key in here would be a second, weaker copy
 * of a partition that has already been made.
 */
export function statementAnchors(
  statementBalances: StatementBalance[],
): Map<string, { statementId: string; endsaldoEur: number }> {
  const byDate = new Map<string, StatementBalance[]>();
  for (const s of statementBalances) {
    if (!s.end_date || !isIsoDate(s.end_date)) continue;
    if (s.endsaldo_eur == null) continue;
    if (typeof s.endsaldo_eur !== 'number' || !Number.isFinite(s.endsaldo_eur)) continue;
    const group = byDate.get(s.end_date);
    if (group) group.push(s);
    else byDate.set(s.end_date, [s]);
  }

  const anchors = new Map<string, { statementId: string; endsaldoEur: number }>();
  for (const [endDate, rows] of byDate) {
    if (rows.some((r) => r.contested === true)) continue;
    if (rows.some((r) => r.anchor_adopted === true)) continue;
    const distinct = new Set(rows.map((r) => round2(r.endsaldo_eur as number)));
    if (distinct.size > 1) continue;
    // Ties among IDENTICAL values resolve on statement_id, so the residual's
    // provenance is deterministic across runs.
    const chosen = rows.reduce((a, b) => (String(a.statement_id) <= String(b.statement_id) ? a : b));
    anchors.set(endDate, {
      statementId: String(chosen.statement_id ?? ''),
      endsaldoEur: round2(chosen.endsaldo_eur as number),
    });
  }
  return anchors;
}

export function computeBalanceSeries(
  config: LiquiditaetConfig | null | undefined,
  bankFacts: BankTransaction[],
  statementBalances: StatementBalance[],
  planned: PlannedTransaction[],
  today?: string,
): BalanceSeriesResult {
  // No stored config (or a malformed one with no anchor date) → fall back to a
  // synthesized default rather than a dead state. The '' anchor never reaches
  // addMonths('') → RangeError because defaultConfig builds a real date.
  // HOF-108 — whether the anchor is EVIDENCE or a placeholder decides whether the
  // period-closure rule below may fire. See ``suppressClosedPeriods``.
  const configIsReal = config != null && Boolean(config.anchor_date);
  const effectiveConfig = configIsReal ? config : defaultConfig(planned);
  if (effectiveConfig == null) {
    // No plan to anchor to (no config, no planned rows) — empty + green, no
    // warning. (Uses the default limit so the copy reads consistently.)
    return {
      dailySeries: [],
      verdict: buildVerdict([], DEFAULT_KK_LIMIT_EUR),
      firstProblemMonth: null,
      istHorizon: '',
      istHorizonAdvanced: false,
      verdictHorizon: '',
      statementResiduals: [],
      reconciled: null,
    };
  }

  const anchorIso = effectiveConfig.anchor_date;
  const anchorBal = effectiveConfig.anchor_balance_eur;
  // Horizon end = the later of anchor+12mo and the END of the (today+12mo)
  // month, so the forward projection fills the chart's fixed window out to
  // today+12mo. Omitting ``today`` (defaulting to the anchor) preserves the
  // original anchor+12mo horizon — every existing caller is unchanged.
  const anchorHorizon = addMonths(anchorIso, WINDOW_MONTHS);
  const todayHorizon =
    today && today > anchorIso
      ? lastDayOfMonth(addMonthsToYm(today.slice(0, 7), WINDOW_MONTHS))
      : anchorHorizon;
  const seriesEnd = todayHorizon > anchorHorizon ? todayHorizon : anchorHorizon;

  // All bank facts with a real booking date, bucketed once. The truthy guard is
  // load-bearing — a '' booking_date would otherwise become the earliest
  // historyStart and the backward day loop would never reach the '' sentinel,
  // hanging the page (codex P2). ``history`` (≤ anchor) drives the backward walk;
  // the slice in (anchor, istHorizon] drives the new ACTUAL region of the forward
  // walk (#1639) — previously those post-anchor payeds were dropped from the curve.
  const validBank = bankFacts.filter((b) => b.booking_date);
  const bankByDay = bucketByDay(validBank, (b) => b.booking_date, (b) => b.amount_eur);
  const history = validBank.filter((b) => b.booking_date <= anchorIso);

  // Ist horizon (#1639): the last day backed by an ACTUAL — the latest payed
  // booking_date or statement_balance end_date — capped at ``today`` so a
  // post-dated payed never draws solid past Heute, floored at the anchor so the
  // existing fixtures (no actuals beyond the anchor) collapse back to anchor_date,
  // and bounded by the drawn window. ``anchor_date``/``anchor_balance_eur`` stay
  // the reconciliation seed; Ist is walked forward from the anchor through actuals.
  // ``isIsoDate`` guards each candidate (not a bare ``>`` string compare): a
  // malformed imported date ('not-a-date') sorts AFTER valid ISO dates, so an
  // unguarded compare would advance istHorizon — then the ``today`` cap below pins
  // it to Heute, marking the whole anchor→today span solid and suppressing the
  // genuine Prognose rows up to that bogus horizon (could mask a real Engpass: #1661).
  let istHorizon = anchorIso;
  for (const b of validBank) if (isIsoDate(b.booking_date) && b.booking_date > istHorizon) istHorizon = b.booking_date;
  for (const s of statementBalances) {
    if (s.end_date && isIsoDate(s.end_date) && s.end_date > istHorizon) istHorizon = s.end_date;
  }
  if (today && istHorizon > today) istHorizon = today;
  if (istHorizon < anchorIso) istHorizon = anchorIso;
  if (istHorizon > seriesEnd) istHorizon = seriesEnd;
  // #3241: did any confirmed datum actually leave this account's own anchor
  // behind? Read AFTER the caps, deliberately — the question is about the horizon
  // this account actually PUBLISHES, not about the raw evidence behind it.
  //
  // The two readings differ in exactly one reachable case (r3 P2): an anchor at
  // or after ``today``. There a post-anchor actual is capped down to ``today`` and
  // then floored back up to the anchor, so the published horizon is the anchor
  // seed echoed back — which is precisely what "not advanced" means, and the flag
  // must agree or ``aggregateSeries`` would hand that account the only vote in
  // the MIN pool and mark pure future as confirmed Ist. Everywhere else the two
  // coincide: with ``anchor < today`` both ``H_raw`` and ``today`` exceed the
  // anchor so the min still does, and ``seriesEnd >= anchor + 12mo`` can never
  // pull the horizon back onto the anchor.
  const istHorizonAdvanced = istHorizon > anchorIso;


  // History starts at the earliest statement boundary, else the earliest
  // bank fact, else (no history) the anchor itself.
  const candidateStarts = [
    ...statementBalances.map((s) => s.start_date),
    ...history.map((b) => b.booking_date),
  ].filter((d) => d && d <= anchorIso);
  const historyStart = candidateStarts.length
    ? candidateStarts.reduce((min, d) => (d < min ? d : min))
    : anchorIso;

  const anchors = statementAnchors(statementBalances);

  // HOF-108 — the period-closure rule rests on one premise: *the balance at the Ist
  // horizon already contains every movement of every period that ended before it*. Two
  // independent things have to be true before that may be acted on, and each of them was
  // a codex P1 on this change's first review round.
  //
  // 1. **An ATTESTED balance must exist.** ``defaultConfig`` — reached only when a tenant
  //    has no stored config at all — invents ``anchor_date = earliest planned value_date
  //    - 1`` at ``anchor_balance_eur = 0``. Ungated, a LAGGED plan set on such a tenant
  //    loses its first month against a balance no bank ever stated. The first version of
  //    this gate read ``configIsReal || istHorizonAdvanced``, and codex was right to
  //    refuse it: applying one post-anchor transaction to an invented zero yields a
  //    *delta*, not an attested BALANCE. Only a stored anchor (the farmer's own statement
  //    of her Saldo) or an uncontested bank Endzeile carries that authority.
  // 2. **The period itself must be backed by the Ist** — see ``closedByIst``.
  //
  // ``anchors`` is deliberately not date-matched to the period: it is already the set of
  // Endzeilen the series RE-SEEDS on, so any member of it makes this account's balance
  // bank-stated rather than integrated, which is the property the premise needs.
  const balanceIsAttested = configIsReal || anchors.size > 0;

  // The months the Ist actually transacted. A closed period with no booking at all is not
  // "settled by the actuals", it is *unknown* — and suppressing there deletes forecast
  // money against an account that has said nothing about the period. Read off
  // ``validBank`` (history AND post-anchor): the question is whether the money moved, not
  // whether it moved inside the drawn window.
  const actualPeriods = new Set(
    validBank.filter((b) => isIsoDate(b.booking_date)).map((b) => b.booking_date.slice(0, 7)),
  );

  /**
   * Is this plan row's period both CLOSED by the horizon and BACKED by the Ist?
   *
   * The second half is codex round 1's P1 (1 of 2), in the form this pass can honestly
   * close: a period the bank never touched is unknown, not settled. A real stored anchor
   * that postdates the period is the other way it can be backed — the farmer's own Saldo
   * on that day already contains every period that ended before it.
   *
   * What this deliberately does NOT do is make the rule row-grained. Once a closed period
   * has any Ist movement, every lagged plan row for it goes, including one genuinely still
   * unpaid — HOF-108 §7 option 2 (per-``(period, ledger_category)`` actual-vs-plan
   * netting) is the fix and is scoped out of this pass, because it needs the ``period_ym``
   * ⇄ booking-date mapping §6(a)'s lag disagreement makes ambiguous. Two things bound the
   * exposure: ``buildPlannedInput`` re-derives ``period_ym`` from ``value_date``, so a
   * farmer-written plan is lagless and this rule can never reach it; and a per-invoice
   * payable is ``contracted``, which never reaches the engine at all.
   */
  const closedByIst = (periodYm: string | undefined): boolean => {
    if (!balanceIsAttested) return false;
    if (!periodClosedByHorizon(periodYm, istHorizon)) return false;
    // ``periodClosedByHorizon`` has already validated the shape.
    const ym = periodYm as string;
    if (actualPeriods.has(ym)) return true;
    return configIsReal && lastDayOfMonth(ym) <= anchorIso;
  };

  // Backward walk: balance(anchor) = anchorBal; balance(d-1) = balance(d) − change(d).
  // HOF-97 L5.1 — it re-seeds too: the invariant is "at every statement date", not "at
  // every statement date after the config anchor", and drawn history that crosses a
  // statement must show that statement's Saldo. No residual is emitted here — going
  // backwards the accumulation runs the other way, so the gap a re-seed closes is not
  // the "Endzeile_n − (Endzeile_{n-1} + Σ flows)" quantity §6 L5.3 defines, and
  // publishing it under that name would report a different measurement than the one an
  // operator would act on.
  // HOF-97 — a statement dated exactly ON the config anchor replaces the anchor's own
  // balance, and BOTH walks must start from the replacement. Re-seeding only inside the
  // backward loop rendered the Endzeile on the anchor day and then jumped back to the
  // stale config value the next day — a visible one-day cliff (codex round 1, P1).
  // Resolved once, here, so the two walks cannot disagree about their shared origin.
  const anchorAtSeed = anchors.get(anchorIso);
  const seedBal = anchorAtSeed ? anchorAtSeed.endsaldoEur : round2(anchorBal);
  // ...and it is RECONCILED like any other (codex round 2, P1). The residual loop below
  // starts at anchor+1, so a seed-date statement was silently used and never measured: its
  // Endsaldo could differ arbitrarily from the configured anchor while statementResiduals
  // stayed empty and ``reconciled`` read ``null`` — "never checked" — leaving the surface
  // with nothing to disclose. Here the expectation IS the configured anchor: rival claims
  // about the same account on the same day, and their gap is what a residual measures.
  const seedResiduals: StatementResidual[] = anchorAtSeed
    ? [
        {
          statementId: anchorAtSeed.statementId,
          endDate: anchorIso,
          endsaldoEur: anchorAtSeed.endsaldoEur,
          expectedEur: round2(anchorBal),
          residualEur: round2(anchorAtSeed.endsaldoEur - round2(anchorBal)),
        },
      ]
    : [];

  const historyDesc: DailyBalancePoint[] = [];
  let cur = seedBal;
  for (let d = anchorIso; d >= historyStart; d = addDays(d, -1)) {
    const anchorHere = anchors.get(d);
    if (anchorHere) cur = anchorHere.endsaldoEur;
    historyDesc.push({ date: d, balanceEur: cur, isActual: true });
    cur = round2(cur - (bankByDay.get(d) ?? 0)); // previous day's end-of-day balance
  }
  const historyAsc = historyDesc.reverse();

  // One continuous forward walk from the anchor (seeded once at anchorBal, never
  // re-seeded, so the running balance stays continuous across the anchor): each
  // day ≤ istHorizon applies the ACTUAL bank delta and is tagged isActual (the
  // confirmed window past the anchor — Kontoauszüge land after a payed booking);
  // each day after istHorizon applies the PLANNED delta and is forecast. Starting
  // the projection AFTER istHorizon naturally excludes a planned sibling of an
  // already-payed record (both ≤ istHorizon) with no special-casing.
  //
  // HOF-108 — ...but only when the plan and its payment share a transaction_id. A
  // top-down BWA row has no payed sibling to be superseded by, so a payment lag that
  // dates a CLOSED period's plan past the horizon put it in the projection on top of the
  // actuals it describes: WF's forecast peaked at 3.361.510,92 €, ~2x the farm's
  // all-time Ist maximum, on one +2.420.427,69 € day that was July's BWA revenue counted
  // a second time. ``periodClosedByHorizon`` is the period-level guard identity
  // supersession cannot express; the two are complementary and both run.
  const plannedByDay = bucketByDay(
    planned.filter(
      (p) =>
        p.value_date &&
        p.value_date > istHorizon &&
        p.value_date <= seriesEnd &&
        !closedByIst(p.period_ym),
    ),
    (p) => p.value_date,
    signedPlanned,
  );
  const forward: DailyBalancePoint[] = [];
  const statementResiduals: StatementResidual[] = [...seedResiduals];
  let prev = seedBal;
  for (let d = addDays(anchorIso, 1); d <= seriesEnd; d = addDays(d, 1)) {
    let isActual: boolean;
    if (d <= istHorizon) {
      prev = round2(prev + (bankByDay.get(d) ?? 0));
      isActual = true;
    } else {
      prev = round2(prev + (plannedByDay.get(d) ?? 0));
      isActual = false;
    }
    // HOF-97 L5.2/L5.3 — the re-seed, and the measurement it makes possible. The
    // residual is read BEFORE ``prev`` is overwritten: the whole quantity is "what our
    // integral said" minus "what the bank says", and after the assignment that
    // difference is gone. Emitted for forecast days too — a statement whose end_date
    // lands past the Ist horizon is still the bank's word, and withholding the anchor
    // there would leave a known-true balance unshown.
    const anchorHere = anchors.get(d);
    if (anchorHere) {
      statementResiduals.push({
        statementId: anchorHere.statementId,
        endDate: d,
        endsaldoEur: anchorHere.endsaldoEur,
        expectedEur: prev,
        residualEur: round2(anchorHere.endsaldoEur - prev),
      });
      prev = anchorHere.endsaldoEur;
    }
    forward.push({ date: d, balanceEur: prev, isActual });
  }

  const dailySeries = [...historyAsc, ...forward];
  // The verdict + breach only judge genuine FORECAST (post-istHorizon), never the
  // newly-actual window — same contract as before (it ran on the !isActual points).
  const projection = forward.filter((p) => !p.isActual);
  const verdict = buildVerdict(projection, effectiveConfig.kk_limit_eur);

  return {
    dailySeries,
    verdict,
    firstProblemMonth: verdict.firstProblemYm,
    istHorizon,
    istHorizonAdvanced,
    // Per-account the rendered and the judged boundary are the same thing; only
    // the "Alle Konten" aggregate ever widens one without the other (#3241).
    verdictHorizon: istHorizon,
    statementResiduals,
    // Three states, not two (HOF-97 §6 L5.4). ``null`` is "never checked against a bank
    // statement", and it is NOT ``true``: conflating them is how an unreconciled
    // integral passed for a reconciled balance for a year.
    reconciled: statementResiduals.length
      ? statementResiduals.every((r) => Math.abs(r.residualEur) <= STATEMENT_RESIDUAL_TOLERANCE_EUR)
      : null,
  };
}
